import { useState } from 'react';

const PRESETS = [
  'Unsichere Waffenhandhabung',
  'Unbeabsichtigte Schussabgabe',
  'Verletzung der 180°-Regel',
  'Sweeping',
  'Fallengelassene Waffe',
  'Unsportliches Verhalten',
] as const;
const OTHER = 'other';

/**
 * Bottom sheet for the DQ reason (UX review REF-20): preset reasons plus
 * "Anderer Grund" with free text. Replaces the old `window.prompt`.
 */
export function DqReasonSheet({
  shooterLabel,
  onCancel,
  onConfirm,
}: {
  shooterLabel: string;
  onCancel: () => void;
  /** Resolves when the call is done; the parent closes the sheet on success. */
  onConfirm: (reason: string) => Promise<void>;
}) {
  const [choice, setChoice] = useState<string | null>(null);
  const [other, setOther] = useState('');
  const [busy, setBusy] = useState(false);
  const reason = choice === OTHER ? other.trim() : choice;

  async function submit() {
    if (!reason || busy) return;
    setBusy(true);
    try {
      await onConfirm(reason);
    } finally {
      setBusy(false);
    }
  }

  const option = (value: string, label: string) => (
    <button
      key={value}
      type="button"
      role="radio"
      aria-checked={choice === value}
      onClick={() => setChoice(value)}
      disabled={busy}
      className={`min-h-12 w-full rounded-lg border px-4 py-2 text-left text-sm font-medium disabled:opacity-50 ${
        choice === value
          ? 'border-red-500 bg-red-950/50 text-red-100'
          : 'border-zinc-700 bg-zinc-900 text-zinc-200 hover:border-zinc-500'
      }`}
    >
      {label}
    </button>
  );

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-black/70 sm:items-center"
      onClick={() => !busy && onCancel()}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="dq-sheet-title"
        className="max-h-[90vh] w-full max-w-md overflow-y-auto rounded-t-2xl bg-zinc-900 p-4 sm:rounded-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 id="dq-sheet-title" className="text-lg font-bold">
          Disqualifizieren
        </h3>
        <p className="mb-3 truncate text-sm text-zinc-400">{shooterLabel} — Grund wählen</p>
        <div role="radiogroup" aria-label="Grund" className="space-y-2">
          {PRESETS.map((p) => option(p, p))}
          {option(OTHER, 'Anderer Grund …')}
        </div>
        {choice === OTHER && (
          <label className="mt-3 block">
            <span className="text-sm text-zinc-400">Grund</span>
            <input
              type="text"
              value={other}
              onChange={(e) => setOther(e.target.value)}
              maxLength={500}
              autoFocus
              disabled={busy}
              className="mt-1 w-full rounded border border-zinc-700 bg-zinc-950 px-3 py-3 text-base"
            />
          </label>
        )}
        <div className="mt-4 flex justify-between gap-2">
          <button
            type="button"
            onClick={onCancel}
            disabled={busy}
            className="min-h-12 rounded border border-zinc-700 px-4 py-2 text-sm text-zinc-300 disabled:opacity-30"
          >
            Abbrechen
          </button>
          <button
            type="button"
            onClick={() => void submit()}
            disabled={!reason || busy}
            className="min-h-12 rounded bg-red-700 px-4 py-2 text-sm font-bold text-white hover:bg-red-600 disabled:opacity-30"
          >
            {busy ? 'Wird gespeichert …' : '❌ Disqualifizieren'}
          </button>
        </div>
      </div>
    </div>
  );
}
